/**
 * Reusable reporting cadence picker — "Report every [N] [unit] [day toggles]"
 *
 * Extracted from ScheduleThermometer / BudgetThermometer so the same widget
 * can be used in:
 *   - Project Config → Advanced tab (project-level reporting cadence)
 *   - Manage Curve modal (schedule / finance curve cadence)
 *   - Mission sub-KPI config (future)
 *
 * Uses CSS custom-property tokens and logical properties for theme + RTL support.
 */
import React, { useCallback } from "react";
import type { ReportingCadence } from "../../kpi-engine/reportingDates";

// ── Types ──────────────────────────────────────────────────────

export type MonthlyRuleMode = "day" | "first" | "last";

export type CadenceValue = {
  cadence: ReportingCadence;
  interval: number;
  weekdays: boolean[];         // length 7, index 0 = Sun
  monthlyMode: MonthlyRuleMode;
  monthlyDayOfMonth: number;   // 1-31
  monthlyWeekday: number;      // 0=Sun … 6=Sat
};

export type ReportingCadenceWidgetProps = {
  value: CadenceValue;
  onChange: (next: CadenceValue) => void;
  disabled?: boolean;
  /** Show "semi-annual" and "annual" cadence options (project-level only). */
  showExtendedCadences?: boolean;
  /** Label shown before the widget. Default: "Report every" */
  label?: string;
};

// ── Defaults ───────────────────────────────────────────────────

export const DEFAULT_CADENCE_VALUE: CadenceValue = {
  cadence: "monthly",
  interval: 1,
  weekdays: [false, false, false, false, false, true, false], // Friday default
  monthlyMode: "day",
  monthlyDayOfMonth: 1,
  monthlyWeekday: 1,
};

// ── Shared inline styles (theme-token based) ───────────────────

const controlStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid var(--border-default)",
  background: "var(--bg-hover)",
  color: "var(--text-primary)",
};

const dayBtnBase: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 99,
  cursor: "pointer",
  fontWeight: 650,
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

// ── Component ──────────────────────────────────────────────────

export function ReportingCadenceWidget({
  value,
  onChange,
  disabled = false,
  showExtendedCadences = false,
  label = "Report every",
}: ReportingCadenceWidgetProps) {
  const {
    cadence,
    interval,
    weekdays,
    monthlyMode,
    monthlyDayOfMonth,
    monthlyWeekday,
  } = value;

  const update = useCallback(
    (patch: Partial<CadenceValue>) => onChange({ ...value, ...patch }),
    [value, onChange],
  );

  const anyWeekdaySelected = weekdays.some(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Row 1: "Report every [N] [unit]" */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, opacity: 0.9, flexShrink: 0 }}>{label}</span>

        <input
          disabled={disabled}
          type="number"
          min={1}
          value={interval}
          onChange={(e) => update({ interval: Math.max(1, Number(e.target.value || 1)) })}
          style={{ ...controlStyle, width: 84 }}
        />

        <select
          disabled={disabled}
          value={cadence}
          onChange={(e) => update({ cadence: e.target.value as ReportingCadence })}
          style={controlStyle}
        >
          <option value="daily">day</option>
          <option value="weekly">week</option>
          <option value="monthly">month</option>
          <option value="quarterly">quarter</option>
          {showExtendedCadences && (
            <>
              <option value="semi-annual">semi-annual</option>
              <option value="annual">annual</option>
            </>
          )}
        </select>

        {/* Weekly: day-of-week toggles */}
        {cadence === "weekly" && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            {([
              { i: 1, label: "M" },
              { i: 2, label: "T" },
              { i: 3, label: "W" },
              { i: 4, label: "T" },
              { i: 5, label: "F" },
              { i: 6, label: "S" },
              { i: 0, label: "S" },
            ] as { i: number; label: string }[]).map((d) => (
              <button
                key={`${d.i}-${d.label}`}
                type="button"
                disabled={disabled}
                onClick={() => {
                  const next = [...weekdays];
                  next[d.i] = !next[d.i];
                  update({ weekdays: next });
                }}
                style={{
                  ...dayBtnBase,
                  border: weekdays[d.i]
                    ? "1px solid rgba(56,132,255,0.5)"
                    : "1px solid rgba(200,200,200,0.4)",
                  background: weekdays[d.i]
                    ? "rgba(56, 132, 255, 0.35)"
                    : "var(--text-primary)",
                  color: weekdays[d.i] ? "var(--text-primary)" : "#222",
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {d.label}
              </button>
            ))}
            {!anyWeekdaySelected && (
              <div style={{ fontSize: 12, opacity: 0.85, marginInlineStart: 8 }}>
                <span style={{ color: "var(--color-warning)" }}>⚠</span> Select day(s)
              </div>
            )}
          </div>
        )}
      </div>

      {/* Row 2 (monthly only): "on [day # / first / last] [N / weekday]" */}
      {cadence === "monthly" && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, paddingInlineStart: 4 }}>
          <span style={{ fontSize: 12, opacity: 0.9, flexShrink: 0 }}>on</span>

          <select
            disabled={disabled}
            value={monthlyMode}
            onChange={(e) => update({ monthlyMode: e.target.value as MonthlyRuleMode })}
            style={{ ...controlStyle, flexShrink: 0 }}
          >
            <option value="day">day #</option>
            <option value="first">first</option>
            <option value="last">last</option>
          </select>

          {monthlyMode === "day" ? (
            <input
              disabled={disabled}
              type="number"
              min={1}
              max={31}
              value={monthlyDayOfMonth}
              onChange={(e) =>
                update({ monthlyDayOfMonth: Math.max(1, Math.min(31, Number(e.target.value || 1))) })
              }
              style={{ ...controlStyle, width: 90 }}
            />
          ) : (
            <select
              disabled={disabled}
              value={monthlyWeekday}
              onChange={(e) => update({ monthlyWeekday: Number(e.target.value) })}
              style={controlStyle}
            >
              <option value={0}>Sunday</option>
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
            </select>
          )}
        </div>
      )}
    </div>
  );
}

export default ReportingCadenceWidget;
